import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SessionCtl } from '../services/session.ctl.service';
import { SessionEventsService } from '../events/session-events.service';
import { CoreUIToastV2Service } from '../services/toast.service';
import { isAccountDisabledError, accountDisabledMessage } from './account-disabled.constant';

/** Same gateway-routed Klab API surface the bearer interceptor covers. */
const KLAB_API_PATH_REGEX =
    /\/ms-(iam|people|organization|master-data|employee|hr|agreement|payroll|report|messaging-ws|notifications?|accounting)\//;

/**
 * Endpoints that own their own error handling and must not be hijacked:
 *  - login: a 401 there means wrong credentials, and the form shows it inline.
 *  - refresh / validate: `SessionRefreshOnFocusEffect` already reacts to a 401
 *    with its own toast; letting this interceptor fire too would log the user
 *    out twice and stack two toasts.
 */
const SELF_HANDLED_AUTH_PATHS = /\/api\/v1\/auth\/(login|refresh|validate)\b/;

/**
 * Ends the session as soon as any Klab API answers 401, instead of waiting for
 * the user to reload the page or refocus the tab.
 *
 * Without this the app only noticed a revoked session on F5 or on window focus,
 * so a terminated employee kept browsing with cached data and a dead token for
 * as long as the tab stayed open — up to the 24h the access token lives.
 *
 * The error is re-thrown untouched so callers keep their own handling; this
 * only adds the session teardown that nobody else was doing.
 */
export const sessionRevokedInterceptor: HttpInterceptorFn = (req, next) => {
    const sessionCtl = inject(SessionCtl);
    const sessionEvents = inject(SessionEventsService);
    const toast = inject(CoreUIToastV2Service);

    return next(req).pipe(
        catchError((err: unknown) => {
            if (!(err instanceof HttpErrorResponse)) return throwError(() => err);
            if (!KLAB_API_PATH_REGEX.test(req.url)) return throwError(() => err);
            if (SELF_HANDLED_AUTH_PATHS.test(req.url)) return throwError(() => err);
            if (!sessionCtl.isLoggedIn) return throwError(() => err);

            const accountDisabled = isAccountDisabledError(err);
            if (err.status !== 401 && !accountDisabled) return throwError(() => err);

            // Se limpia la sesion en local en vez de llamar a logout() del
            // puerto: ese logout dispara otra peticion HTTP que volveria a
            // pasar por aqui, y con el token ya revocado fallaria en bucle.
            sessionCtl.sessionClear();
            sessionEvents.loggedInCheckPublish('logout');

            toast.show({
                data: {
                    type: 'info',
                    title: accountDisabled ? 'Cuenta deshabilitada' : 'Tu sesión terminó',
                    msg: accountDisabled
                        ? accountDisabledMessage(err)
                        : 'Tu sesión ya no es válida. Vuelve a iniciar sesión.',
                    autoClose: true,
                    closable: true,
                    autoCloseTime: 4500,
                },
            });

            return throwError(() => err);
        }),
    );
};
